import numeral from 'numeral';
import * as React from 'react';

import {XAxisFormatValues} from '../generated/graphql';
import {DateFormat, format} from './date';

const ALL_CROSSHAIR_VALUES_REGEX = /(\[\[[^\]]+\]\])/g;
const INSIDE_CROSSHAIR_VALUES_REGEX = /\[\[([^\]]+)\]\]/;

type LegendCrosshairPlotType = 'bar' | 'line' | 'stacked-area' | 'pct-area';
type LegendCrosshairPrimitive = string | number | Date | undefined;
type LegendCrosshairData = {
  x?: {
    xAxis: string;
    val: string | number | Date;
    xAxisFormat?: XAxisFormatValues;
    prevXVal?: string | number | Date;
  };
  y?: LegendCrosshairPrimitive;
  mean?: LegendCrosshairPrimitive;
  min?: LegendCrosshairPrimitive;
  max?: LegendCrosshairPrimitive;
  stddev?: LegendCrosshairPrimitive;
  count?: LegendCrosshairPrimitive;
  percent?: LegendCrosshairPrimitive;
  total?: LegendCrosshairPrimitive;
  original?: LegendCrosshairPrimitive;
};
type LegendCrosshairScalarKey = Exclude<keyof LegendCrosshairData, 'x'>;
type LegendCrosshairTruncationType = 'Beginning' | 'Middle' | 'End';

const fontStyle = {fontFamily: 'Source Sans 3'};

function formatTemplateBySigDigits(sigDigits: number) {
  return `0.[${'0'.repeat(Math.max(1, sigDigits))}]`;
}

function removeTrailingZeros(stringNum: string) {
  if (stringNum.includes('e')) {
    return stringNum;
  }
  while (stringNum.endsWith('0')) {
    stringNum = stringNum.slice(0, -1);
  }

  if (stringNum.endsWith('.')) {
    return stringNum.slice(0, -1);
  }

  return stringNum;
}

function formatLegendValue(value: LegendCrosshairPrimitive) {
  let displayValue: LegendCrosshairPrimitive | Record<string, unknown> = value;
  if (
    typeof displayValue === 'string' &&
    displayValue.length > 0 &&
    displayValue[0] === '{' &&
    displayValue[displayValue.length - 1] === '}'
  ) {
    try {
      displayValue = JSON.parse(displayValue) as Record<string, unknown>;
    } catch {
      displayValue = value;
    }
  }
  if (displayValue == null) {
    return '-';
  } else if (typeof displayValue === 'number') {
    if (!Number.isFinite(displayValue)) {
      return displayValue.toString();
    }
    if (Number.isInteger(displayValue)) {
      return displayValue.toString();
    }

    const sigDigits = 5;
    if (displayValue < 1 && displayValue > -1) {
      return removeTrailingZeros(displayValue.toPrecision(sigDigits));
    }
    return numeral(displayValue).format(formatTemplateBySigDigits(sigDigits));
  } else if (typeof displayValue === 'string') {
    return displayValue;
  }

  return JSON.stringify(displayValue);
}

function isDatetimeAxisFormat(
  xAxis: string,
  xAxisFormat: XAxisFormatValues | undefined
) {
  return (
    xAxis === '_timestamp' ||
    xAxis === 'Wall Time' ||
    xAxisFormat === XAxisFormatValues.Datetime
  );
}

function formatXAxisValue(
  xAxis: string,
  val: string | number | Date,
  xAxisFormat?: XAxisFormatValues
) {
  if (isDatetimeAxisFormat(xAxis, xAxisFormat)) {
    if (typeof val === 'string') {
      val = Number.parseFloat(val);
    }
    return format(new Date(val), DateFormat.MONTH_DAY_HOUR_MIN);
  }
  return formatLegendValue(val);
}

function truncateLegendString(
  s: string,
  maxLength = 30,
  runNameTruncationType: LegendCrosshairTruncationType = 'Middle'
) {
  if (s.length <= maxLength) {
    return s;
  }

  if (runNameTruncationType === 'Beginning') {
    return '…' + s.substr(-maxLength);
  }

  if (runNameTruncationType === 'End') {
    return s.substr(0, maxLength) + '…';
  }

  if (maxLength < 4) {
    return s.substr(0, Math.max(1, maxLength)) + '…';
  }
  const rightLength = Math.floor(maxLength / 2) - 1;
  const leftLength = maxLength - rightLength - 1;
  const leftSide = s.substr(0, leftLength);
  const rightSide = s.substr(-rightLength);
  return leftSide + '…' + rightSide;
}

export function defaultCrosshairValues({
  isGrouped,
  isFullFidelity,
  isSmoothed,
  type,
}: {
  isGrouped: boolean;
  isFullFidelity: boolean;
  isSmoothed: boolean;
  type: LegendCrosshairPlotType;
}) {
  /* eslint-disable no-template-curly-in-string */

  if (type === 'pct-area') {
    return ' [[ ${x}: ${y} (${percent}%) ]]';
  } else if (type === 'stacked-area') {
    return ' [[ ${x}: ${y} ]]';
  } else if (type === 'bar') {
    if (isGrouped) {
      return ' [[ ${mean} σ ${stddev} (${min}, ${max}) ]]';
    } else {
      return ' [[ ${x} ]]';
    }
  } else {
    if (isGrouped && !isFullFidelity) {
      return ' [[ ${x}: ${mean} σ ${stddev} (${min}, ${max}) ]]';
    } else if (isGrouped && isFullFidelity) {
      return ' [[ ${x}: ${mean} (${min}, ${max}) ]]';
    } else if (isSmoothed) {
      return ' [[ ${x}: ${y} (${original})]]';
    } else {
      return ' [[ ${x}: ${y} ]]';
    }
  }
  /* eslint-enable no-template-curly-in-string */
}

export function legendTemplateInsertCrosshairValues({
  addDefaultTemplateIfCrosshairValuesMissing,
  key,
  legendTemplate,
  maxLength = 10,
  simpleVersion = false,
  truncateOutsideOfCrosshairValues = false,
  type,
  values,
  runNameTruncationType,
}: {
  addDefaultTemplateIfCrosshairValuesMissing: boolean;
  key: string;
  legendTemplate: string;
  maxLength?: number;
  simpleVersion?: boolean;
  truncateOutsideOfCrosshairValues?: boolean;
  type: LegendCrosshairPlotType;
  values: LegendCrosshairData;
  runNameTruncationType?: LegendCrosshairTruncationType;
}) {
  let fixedLegendTemplate = legendTemplate;
  if (
    addDefaultTemplateIfCrosshairValuesMissing &&
    legendTemplate.match(ALL_CROSSHAIR_VALUES_REGEX) == null
  ) {
    fixedLegendTemplate =
      legendTemplate +
      defaultCrosshairValues({
        isGrouped: false,
        isSmoothed: false,
        isFullFidelity: false,
        type,
      });
  }
  const squareBracketMatches = fixedLegendTemplate.split(
    ALL_CROSSHAIR_VALUES_REGEX
  );
  return (
    <span key={'crosshair-val' + key + legendTemplate}>
      {squareBracketMatches.map((squareBracketMatch, i) => {
        if (squareBracketMatch.match(INSIDE_CROSSHAIR_VALUES_REGEX)) {
          const squareBracketMatchNoBracket = squareBracketMatch.match(
            INSIDE_CROSSHAIR_VALUES_REGEX
          );
          if (
            squareBracketMatchNoBracket &&
            squareBracketMatchNoBracket.length > 1
          ) {
            const matches =
              squareBracketMatchNoBracket[1].split(/(\${[^}]+})/g);
            return (
              // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
              <span key={'legend-chunk' + i}>
                {matches.map((match, j) => {
                  if (match.match(/\${[^}]+}/)) {
                    const keyStr = match
                      .replace(/^\${\s*/, '')
                      .replace(/\s*}$/, '');
                    if (keyStr === 'x' && values.x) {
                      const keyPrefix =
                        type === 'line' ? 'x-axis-val' : 'legend-val';
                      return (
                        // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                        <strong key={`${keyPrefix} ${j}`}>
                          {values.x.prevXVal
                            ? `${formatXAxisValue(
                                values.x.xAxis,
                                values.x.prevXVal,
                                values.x.xAxisFormat
                              )}-`
                            : ''}
                          {formatXAxisValue(
                            values.x.xAxis,
                            values.x.val,
                            values.x.xAxisFormat
                          )}
                        </strong>
                      );
                    } else {
                      const value = Object.prototype.hasOwnProperty.call(
                        values,
                        keyStr
                      )
                        ? formatLegendValue(
                            values[keyStr as LegendCrosshairScalarKey]
                          )
                        : undefined;
                      // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                      return <strong key={'legend-val ' + j}>{value}</strong>;
                    }
                  } else {
                    if (match.match(/^\s+$/)) {
                      // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                      return <span key={'whitespace ' + j}></span>;
                    } else {
                      // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                      return <span key={'match ' + j}>{match}</span>;
                    }
                  }
                })}
              </span>
            );
          } else {
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            return <span key={'empty ' + i}></span>;
          }
        } else {
          if (truncateOutsideOfCrosshairValues) {
            squareBracketMatch = truncateLegendString(
              squareBracketMatch,
              maxLength,
              runNameTruncationType
            );
          }
          return !simpleVersion ? (
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            <span key={'legend-chunk' + i} style={fontStyle}>
              {squareBracketMatch}
            </span>
          ) : null;
        }
      })}
    </span>
  );
}
